/**
 * Error type parsing utilities for flow interview errors
 */

export const ERROR_TYPE_DISPLAY = {
  'INVALID_FIELD': 'Missing Fields',
  'NO_DATA_FOUND': 'No Records Found',
  'VALIDATION_ERROR': 'Validation Failed',
  'INSUFFICIENT_ACCESS': 'Permission Denied',
  'LOCK_ERROR': 'Record Locked',
  'SOQL_LIMIT': 'Query Limit Exceeded',
  'DML_LIMIT': 'DML Limit Exceeded',
  'CPU_LIMIT': 'CPU Time Exceeded',
  'QUERY_ERROR': 'Query Error',
  'DML_ERROR': 'Data Modification Error',
  'UNABLE_TO_LOCK_ROW': 'Record Lock Error',
  'DUPLICATE_VALUE': 'Duplicate Record',
  'REQUIRED_FIELD_MISSING': 'Required Field Missing',
  'LIMIT_EXCEEDED': 'Governor Limit Exceeded',
  'UNKNOWN': 'Unknown Error',
  'OTHER': 'Other Errors'
} as const;

export type ErrorType = keyof typeof ERROR_TYPE_DISPLAY;

export interface ParsedError {
  type: ErrorType;
  coreMessage: string;
}

/**
 * Parse a flow error message to extract the error type and core message
 */
export function parseFlowError(errorMessage: string | null): ParsedError {
  if (!errorMessage) {
    return { type: 'OTHER', coreMessage: errorMessage || '' };
  }
  
  // Extract core error after "ERROR at Row:X:Column:Y"
  const pattern = /ERROR at Row:\d+:Column:\d+\s+([^.]+)\./;
  const match = errorMessage.match(pattern);
  const coreMessage = match?.[1]?.trim() || errorMessage;
  
  // Determine type
  let type: ErrorType = 'OTHER';
  const msg = errorMessage.toLowerCase();
  
  if (msg.includes('no such column') || msg.includes('invalid field')) {
    type = 'INVALID_FIELD';
  } else if (msg.includes('list has no rows') || msg.includes('no rows for assignment')) {
    type = 'NO_DATA_FOUND';
  } else if (msg.includes('validation')) {
    type = 'VALIDATION_ERROR';
  } else if (msg.includes('insufficient access')) {
    type = 'INSUFFICIENT_ACCESS';
  } else if (msg.includes('unable to lock row')) {
    type = 'UNABLE_TO_LOCK_ROW';
  } else if (msg.includes('too many soql queries')) {
    type = 'SOQL_LIMIT';
  } else if (msg.includes('too many dml')) {
    type = 'DML_LIMIT';
  } else if (msg.includes('cpu time limit')) {
    type = 'CPU_LIMIT';
  } else if (msg.includes('duplicate value')) {
    type = 'DUPLICATE_VALUE';
  } else if (msg.includes('required field missing')) {
    type = 'REQUIRED_FIELD_MISSING';
  } else if (msg.includes('limit exceeded')) {
    type = 'LIMIT_EXCEEDED';
  } else if (msg.includes('tried to look up records') || msg.includes('soql')) {
    type = 'QUERY_ERROR';
  } else if (msg.includes('tried to create') || msg.includes('tried to update') || msg.includes('tried to delete') || msg.includes('dml')) {
    type = 'DML_ERROR';
  }
  
  return { type, coreMessage };
}

/**
 * Get a user-friendly display name for an error type
 */
export function getErrorTypeDisplay(errorType: string): string {
  return ERROR_TYPE_DISPLAY[errorType as ErrorType] || errorType;
}

/**
 * Extract the core error message from a full error string
 */
export function extractCoreErrorMessage(errorMessage: string): string {
  if (!errorMessage) return '';
  
  // Try to extract message after ERROR at Row:X:Column:Y pattern
  const pattern = /ERROR at Row:\d+:Column:\d+\s+([^.]+)\./;
  const match = errorMessage.match(pattern);
  if (match?.[1]) {
    return match[1].trim();
  }
  
  // Fallback: return first sentence or first 100 chars
  const firstSentence = errorMessage.split('.')[0];
  return firstSentence.length > 100 ? firstSentence.substring(0, 100) + '...' : firstSentence;
}